import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { pdf } from "@react-pdf/renderer";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "../firebaseconfig";
import { KeyUri } from "../key";
import {
  Check,
  Copy,
  MapPin,
  Calendar,
  Users,
  ShieldCheck,
  Download,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Phone,
  Sparkles,
} from "lucide-react";
import WellnessInvoicePDF from "./WellnessInvoicePDF";

function PhoneAction({ phone, label = 'Contact', size = 'small' }) {
  const [copied, setCopied] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const isMobile = typeof window !== 'undefined' ? window.innerWidth <= 768 : false;

  const handleCopy = async () => {
    if (!phone) {
      alert('Phone number not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
    } catch (err) {
      console.warn("Clipboard failed:", err);
      const textArea = document.createElement("textarea");
      textArea.value = phone;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
    }
  };

  const handleDial = () => {
    window.open(`tel:${phone.replace(/[^0-9+]/g, '')}`, '_blank');
  };

  const handleClick = () => {
    if (isMobile) {
      handleDial();
    } else {
      setShowCopyModal(true);
    }
  };

  const closeModal = () => {
    setShowCopyModal(false);
    setCopied(false);
  };

  if (!phone) return null;

  return (
    <>
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-center gap-3 p-3 rounded-xl border-2 font-semibold transition-all ${
          isMobile
            ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 shadow-lg hover:shadow-xl hover:-translate-y-0.5'
            : 'bg-white hover:bg-emerald-50 border-emerald-300 hover:border-emerald-500 text-emerald-700 shadow-sm hover:shadow-md'
        } ${size === 'medium' ? 'text-base py-3.5' : 'text-sm py-2.5'}`}
      >
        <Phone size={20} className={isMobile ? 'text-white' : 'text-emerald-600'} />
        {label}
        {!isMobile && <Copy size={18} className="text-emerald-400 ml-auto" />}
      </button>

      {/* Copy Modal */}
      {showCopyModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-50 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Modal */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
            <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto border border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Copy size={28} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Copy Phone Number</h3>
                <p className="text-gray-600 mb-6">Tap to copy support number</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border-2 border-dashed border-gray-300 mb-6 text-center">
                <div className="font-mono text-2xl font-bold text-gray-900 tracking-wide break-all">
                  {phone}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCopy}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 text-sm"
                >
                  {copied ? (
                    <>
                      <Check size={20} className="text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={20} />
                      Copy Number
                    </>
                  )}
                </button>
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-xl transition-all text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}


export default function WellnessBookingInvoice() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const bookingIdFromState = location.state?.bookingId;
  const bookingId = searchParams.get("bookingId") || bookingIdFromState;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  // Prevent duplicate auto-generation
  const [invoiceSynced, setInvoiceSynced] = useState(false);
    const [paymentFailed, setPaymentFailed] = useState(false);
const [verifying, setVerifying] = useState(false);
const [verifyTimedOut, setVerifyTimedOut] = useState(false);

const [pricing, setPricing] = useState(null);
useEffect(() => {
  if (!bookingId) { setLoading(false); return; }

  const orderId = searchParams.get("order_id"); // present only on HDFC return
  let cancelled = false;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const PENDING = new Set(["PENDING_VBV", "PENDING", "AUTHORIZING", "STARTED", "NEW"]);

  // poll verify for ~60s while the bank is still processing
  const verifyWithPolling = async () => {
    for (let i = 0; i < 20 && !cancelled; i++) {
      try {
        const r = await axios.post(`${KeyUri.BACKEND_URI}/payments/hdfc/verify`, { orderId });
        if (r.data?.success) return "paid";
        if (!r.data?.pending) return "failed";          // 202 pending falls through to retry
      } catch (err) {
        const status = err.response?.status;
        const orderStatus = err.response?.data?.order_status;
        const retryable = !err.response || status >= 500 || PENDING.has(orderStatus);
        if (!retryable) return "failed";
      }
      await sleep(3000);
    }
    return "timeout";
  };

  // after verify, the booking can briefly still read "pending" (webhook/verify race)
  const fetchBookingSettled = async () => {
    let b = null;
    for (let i = 0; i < 5 && !cancelled; i++) {
      const res = await axios.get(`${KeyUri.BACKEND_URI}/wellness-booking/${bookingId}`);
      b = res.data.booking || res.data.data || res.data;
      if (!orderId || !["pending", "failed"].includes(b.status)) break;
      await sleep(1500);
    }
    return b;
  };

  (async () => {
    try {
      if (orderId) {
        setVerifying(true);
        const result = await verifyWithPolling();
        if (cancelled) return;
        setVerifying(false);
        if (result === "failed") { setPaymentFailed(true); setBooking(null); return; }
        if (result === "timeout") { setVerifyTimedOut(true); setBooking(null); return; }
      }
      const b = await fetchBookingSettled();
      if (!cancelled) setBooking(b);
    } catch (err) {
      console.error("Error fetching wellness booking:", err);
    } finally {
      if (!cancelled) { setVerifying(false); setLoading(false); }
    }
  })();

  return () => { cancelled = true; };
}, [bookingId, searchParams]);

  useEffect(() => {
  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${KeyUri.BACKEND_URI}/tourpricing`);
      setPricing(res.data);
    } catch (err) {
      console.error("Failed to fetch tour pricing:", err);
    }
  };
  fetchPricing();
}, []);
 
  useEffect(() => {
    if (!booking || invoiceSynced) return;

        if (["pending", "failed"].includes(booking.status)) return;
        
    const generateAndUpload = async () => {
      try {
        setInvoiceSynced(true);
     
const isBalanceReturn = searchParams.get("order_id")?.charAt(4) === "B";

        // 1) Generate PDF blob
        const blob = await pdf(
  <WellnessInvoicePDF invoiceData={{ ...booking, pricing, calculatedPaidAmount: paidAmount }} />
).toBlob();

        

        // 2) Upload to Firebase (WELLNESS folder)
        const storageRef = ref(storage, `wellness-pdfs/wellness_${booking._id}.pdf`);
       // console.log(" Auto upload to Firebase:", storageRef.fullPath);
        await uploadBytes(storageRef, blob);
        //console.log("Auto WELLNESS upload complete");

        // 3) Get URL (for debug/backend)
        const downloadURL = await getDownloadURL(storageRef);
       // console.log("Auto WELLNESS PDF UPLOADED:", downloadURL);

        // 4) Trigger WhatsApp confirmation
        if (!isBalanceReturn) {
        try {
          await axios.post(`${KeyUri.BACKEND_URI}/confirm`, {
            booking_idd: booking._id,
          });
          //console.log(" WELLNESS confirmation WhatsApp triggered");
        } catch (confirmErr) {
          console.error(" WELLNESS confirmation failed (non-blocking):", confirmErr);
        }
      }
      } catch (err) {
       // console.error(" Auto WELLNESS PDF FAILED:", err);
        // Silent fail - don't show toast on page load
      }
    };

    generateAndUpload();
  }, [booking, invoiceSynced]);

  const handleDownloadPDF = async () => {
    if (loading || !booking) {
      alert("Loading data. Please wait.");
      return;
    }

    try {
      setIsGeneratingPdf(true);

      // Generate fresh PDF for download
    const blob = await pdf(
  <WellnessInvoicePDF invoiceData={{ ...booking, pricing }} />
).toBlob();

      // Optional: re-upload (harmless overwrite)
      const storageRef = ref(storage, `wellness-pdfs/wellness_${booking._id}.pdf`);
      await uploadBytes(storageRef, blob);

      // Trigger browser download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WellnessBookingInvoice_${booking._id || bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert("✅ Invoice downloaded!");
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      alert("Failed to generate invoice PDF. Please try again.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePayBalanceHdfc = async () => {
  try {
    const res = await axios.post(
      `${KeyUri.BACKEND_URI}/payments/hdfc/create-balance-order`,
      { type: "wellness", bookingId: booking._id },
      { headers: { "Content-Type": "application/json" } }
    );
    if (!res.data?.payment_url) throw new Error("No payment URL returned");
    window.location.href = res.data.payment_url;
  } catch (err) {
    alert(err?.response?.data?.error || err.message || "Could not start balance payment.");
  }
};

  // Generate booking reference in format TGH-WBK-XXXX
  const getBookingRef = (bookingData) => {
    if (!bookingData) return "N/A";
    if (bookingData.bookingReference && bookingData.bookingReference.startsWith('TGH-WBK-')) {
      return bookingData.bookingReference;
    }
    // Extract last 4 digits from _id or generate random
    const idNum = bookingData._id ? bookingData._id.slice(-4).toUpperCase() : Math.floor(1000 + Math.random() * 9000);
    return `TGH-WBK-${idNum}`;
  };

  const handleCopy = () => {
    if (!booking?._id) return;
    const refToCopy = getBookingRef(booking);
    const textArea = document.createElement("textarea");
    textArea.value = refToCopy;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
    document.body.removeChild(textArea);
  };

  useEffect(() => {
 // console.log(" PRICING API FULL:", pricing);
}, [pricing]);

  // Add to Calendar
  const handleAddToCalendar = () => {
    const startDate = booking.startDate || booking.preferredDate;
    if (!startDate) {
      alert("Missing program date");
      return;
    }

    const date = new Date(startDate).toISOString().split("T")[0];
    const title = program.title || booking.packageTitle || "Your Wellness Program";
    const locationStr = "The Great Himalayas Wellness Center";
    const details = `Wellness Program: ${title}\nDate: ${travelDate}\nGuests: ${guestsDescription}\nBooking Ref: ${getBookingRef(booking)}`;

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      title
    )}&dates=${date}T09:00:00/${date}T18:00:00&details=${encodeURIComponent(
      details
    )}&location=${encodeURIComponent(locationStr)}`;

    window.open(googleUrl, "_blank");
  };

 const {
  baseAmount,
  gstAmount,
  totalAmount,
  paidAmount,
  remainingAmount,
  gstRate: appliedGstRate,
} = useMemo(() => {
  if (!booking) {
    return {
      baseAmount: 0,
      gstAmount: 0,
      totalAmount: 0,
      paidAmount: 0,
      remainingAmount: 0,
      gstRate: 0,
    };
  }

  const gstRateValue = Number(
    booking?.accommodation?.gstRate ??
    booking?.accommodation?.gst_rate ??
    booking?.gstRate ??
    booking?.gst_rate ??
    pricing?.gst_price ??
    pricing?.gst ??
    pricing?.data?.gst_price ??
    0
  );

  const total = Number(booking?.totalPrice || booking?.finalAmount || 0);
const paid = Number(
  (booking?.amountPaid > 0 ? booking.amountPaid : null) ?? 
  booking?.advanceAmount ?? 
  total
);
  const remaining = Number(
    booking?.balanceAmount ??
    Math.max(total - paid, 0)
  );

  if (!total || gstRateValue <= 0) {
    return {
      baseAmount: total,
      gstAmount: 0,
      totalAmount: total,
      paidAmount: paid,
      remainingAmount: remaining,
      gstRate: gstRateValue,
    };
  }

  const base = Math.round(total / (1 + gstRateValue / 100));
  const gst = total - base;

  return {
    baseAmount: base,
    gstAmount: gst,
    totalAmount: total,
    paidAmount: paid,
    remainingAmount: remaining,
    gstRate: gstRateValue,
  };
}, [booking, pricing]);

if (verifying) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
      Confirming your payment with the bank. Please don't close or refresh this page...
    </div>
  );
}

if (verifyTimedOut) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
      We haven't received the bank's confirmation yet. If money was deducted, your booking will be
      confirmed automatically and you'll get an email. You can also contact support.
    </div>
  );
}

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm">
        Loading your booking details...
      </div>
    );
  }

    if (paymentFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
        Your payment was not completed. If money was deducted, it will be confirmed shortly. Otherwise please try booking again.
      </div>
    );
  }

    if (booking && ["pending", "failed"].includes(booking.status)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
        Payment for this booking has not been completed.
      </div>
    );
  }
  
  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm">
        No booking details found. Please check your booking ID.
      </div>
    );
  }

  const program = booking.packageId || {};
  const programTitle = booking.packageTitle || program.title || "Wellness Program";
  const programDuration = booking.duration || program.duration || "N/A";

  const travelDate = booking.startDate || booking.preferredDate
    ? new Date(booking.startDate || booking.preferredDate).toLocaleDateString("en-IN", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "N/A";

  const guestsDescription = `${booking.numberOfGuests || booking.guests || 1} Guest${
    (booking.numberOfGuests || booking.guests || 1) > 1 ? "s" : ""
  }`;




  // Use the booking reference helper
  const bookingRef = getBookingRef(booking);

  const isPartiallyPaid = booking?.paymentStatus === "partially_paid";
const isPaid = booking?.paymentStatus === "paid";

const paymentStatusLabel = isPartiallyPaid 
  ? "PARTIALLY PAID" 
  : isPaid 
  ? "PAID" 
  : "PENDING";

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-12 mt-32">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-emerald-100 rounded-full mb-4 ring-8 ring-emerald-50 relative">
            <Check
              size={40}
              className="text-emerald-600 animate-bounce"
              strokeWidth={3}
            />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">
            Wellness Booking Confirmed!
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Get ready for{" "}
            <span className="font-semibold text-gray-900">
              {programTitle}
            </span>
            . A confirmation email has been sent to{" "}
            <span className="font-semibold">{booking.email}</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: main card */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header image */}
              <div className="relative h-48 sm:h-64 bg-gray-200">
                {program.image ? (
                  <img
                    src={program.image}
                    alt={programTitle}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-emerald-200 via-teal-200 to-green-300 flex items-center justify-center">
                    <Sparkles className="w-16 h-16 text-emerald-600 opacity-30" />
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="p-6">
                {/* Title + ID */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                  <div>
                    <h2 className="text-2xl font-bold leading-tight">
                      {programTitle}
                    </h2>
                    <div className="flex items-center text-gray-500 mt-1 text-sm font-medium">
                      <Calendar size={16} className="mr-1.5 text-emerald-500" />
                      <span>{programDuration}</span>
                    </div>
                  </div>

                  <div className="w-full sm:w-auto bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col items-center sm:items-end">
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">
                      Reference ID
                    </span>
                    <button
                      onClick={handleCopy}
                      className="group flex items-center gap-2 text-emerald-700 font-mono text-lg font-bold hover:text-emerald-900 transition-colors"
                    >
                      {bookingRef}
                      {copied ? (
                        <Check size={18} className="text-green-500" />
                      ) : (
                        <Copy
                          size={18}
                          className="text-emerald-300 group-hover:text-emerald-600"
                        />
                      )}
                    </button>
                    {copied && (
                      <span className="text-xs text-green-600 font-medium mt-1">
                        Copied!
                      </span>
                    )}
                  </div>
                </div>

                {/* Timeline: date only */}
                <div className="flex flex-col sm:flex-row gap-6 mb-8 relative">
                  <div className="flex-1 flex flex-row sm:flex-col items-center gap-4 sm:gap-2">
                    <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100 shrink-0">
                      <Calendar size={20} />
                    </div>
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">
                        Program Date
                      </p>
                      <p className="text-lg font-bold">{travelDate}</p>
                    </div>
                  </div>
                </div>

                {/* Guests & status */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row gap-6 sm:items-center justify-between border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-gray-600">
                      <Users size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Guests</p>
                      <p className="text-sm text-gray-600">{guestsDescription}</p>
                    </div>
                  </div>
                  <div className="h-px w-full sm:h-8 sm:w-px bg-gray-200" />
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-gray-600">
                      <ShieldCheck size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">Status</p>
                      <span className="inline-flex px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full border border-emerald-200">
                        {booking.status?.toUpperCase() || "CONFIRMED"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>


              <div className="bg-gray-50/50 p-4 border-t border-gray-100">
                <PhoneAction
                  phone="+919883587965"
                  label="Need Help?"
                  size="medium"
                />
              </div>

            </div>
          </div>

          {/* Right: payment & actions */}
          <div className="space-y-6">
            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleAddToCalendar}
                className="p-4 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 hover:shadow-md transition-all group text-left"
              >
                <Calendar className="h-6 w-6 text-emerald-600 mb-3 group-hover:scale-110 transition-transform origin-left" />
                <span className="block text-sm font-bold">Add to Calendar</span>
                <span className="text-xs text-gray-500">Google, Apple</span>
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPdf}
                className="p-4 bg-white border border-gray-200 rounded-xl hover:border-emerald-300 hover:shadow-md transition-all group text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download className="h-6 w-6 text-emerald-600 mb-3 group-hover:scale-110 transition-transform origin-left" />
                <span className="block text-sm font-bold">Download Invoice</span>
                <span className="text-xs text-gray-500">
                  {isGeneratingPdf ? "Generating..." : "Receipt"}
                </span>
              </button>
            </div>

            {/* Payment summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold">Payment Details</h3>
                <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                 {paymentStatusLabel}
                </span>
              </div>

              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isDetailsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="p-5 space-y-3">
  <div className="flex justify-between text-sm text-gray-600">
  <span>Base Price</span>
  <span className="font-medium text-gray-900">
    ₹{baseAmount.toLocaleString("en-IN")}
  </span>
</div>

<div className="flex justify-between text-sm text-gray-600">
  <span>GST</span>
  <span className="font-medium text-gray-900">
    ₹{gstAmount.toLocaleString("en-IN")}
  </span>
</div>

<div className="pt-4 border-t border-dashed border-gray-200 space-y-2">
  <div className="flex justify-between items-center">
    <span className="font-bold text-gray-900">Total Booking Amount</span>
    <span className="font-bold text-lg text-gray-900">
      ₹{totalAmount.toLocaleString("en-IN")}
    </span>
  </div>

  <div className="flex justify-between items-center">
    <span className="font-semibold text-emerald-700">Amount Paid</span>
    <span className="font-bold text-lg text-emerald-700">
      ₹{paidAmount.toLocaleString("en-IN")}
    </span>
  </div>

  {booking.paymentStatus === "partially_paid" && remainingAmount > 0 && (
    <div className="flex justify-between items-center">
      <span className="font-semibold text-orange-700">Remaining Amount</span>
      <span className="font-bold text-lg text-orange-700">
        ₹{remainingAmount.toLocaleString("en-IN")}
      </span>
    </div>
  )}

  {booking.paymentGateway === "hdfc" &&
  booking.paymentStatus === "partially_paid" &&
  remainingAmount > 0 && (
    <button
      onClick={handlePayBalanceHdfc}
      className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-md transition-colors"
    >
      Pay Balance ₹{remainingAmount.toLocaleString("en-IN")} via HDFC
    </button>
  )}

</div>



                  <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded-md font-mono break-all">
                    Payment ID: {booking.paymentId || booking.razorpayPaymentId || "N/A"}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                className="w-full py-2 bg-gray-50 hover:bg-gray-100 text-xs text-gray-500 font-medium flex items-center justify-center gap-1 transition-colors border-t border-gray-100"
              >
                {isDetailsOpen ? "Hide Details" : "Show Details"}
                {isDetailsOpen ? (
                  <ChevronUp size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
